import { useEffect, lazy, Suspense } from 'react';
import Lenis from 'lenis';
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import '@/App.css';
import './i18n';
import { Toaster } from '@/components/ui/sonner';
import { QuoteProvider } from '@/components/site/QuoteContext';
import { CartProvider } from '@/context/CartContext';
import { LanguageProvider } from '@/context/LanguageContext';
import { SiteDataProvider } from '@/context/SiteDataContext';
import Header from '@/components/site/Header';
import Footer from '@/components/site/Footer';
import SeoManager from '@/components/site/SeoManager';
import WhatsAppWidget from '@/components/site/WhatsAppWidget';

const Home = lazy(() => import('./pages/Home'));
const ProductsHub = lazy(() => import('./pages/ProductsHub'));
const ProductDetail = lazy(() => import('./pages/ProductDetail'));
const Categories = lazy(() => import('./pages/Categories'));
const PaymentSuccess = lazy(() => import('./pages/PaymentSuccess'));
const PaymentCancel = lazy(() => import('./pages/PaymentCancel'));
const BlogListing = lazy(() => import('./pages/BlogListing'));
const BlogDetail = lazy(() => import('./pages/BlogDetail'));
const LegalPage = lazy(() => import('./pages/LegalPage'));
const AboutUs = lazy(() => import('./pages/AboutUs'));
const QualityManufacturing = lazy(() => import('./pages/QualityManufacturing'));
const Downloads = lazy(() => import('./pages/Downloads'));
const Faqs = lazy(() => import('./pages/Faqs'));
const ContactUs = lazy(() => import('./pages/ContactUs'));
const RequestQuotePage = lazy(() => import('./pages/RequestQuotePage'));
const CategoryDetail = lazy(() => import('./pages/CategoryDetail'));
const SpecialtyInstruments = lazy(() => import('./pages/SpecialtyInstruments'));
const NotFound = lazy(() => import('./pages/NotFound'));

const PageLoader = () => (
  <div className="flex h-screen w-screen items-center justify-center bg-white">
    <div className="h-10 w-10 animate-spin rounded-full border-4 border-mt-blue border-t-transparent" />
  </div>
);

const ScrollManager = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

const AnchorHandler = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    const onClick = (e) => {
      const a = e.target.closest('a[href^="#"]');
      if (!a) return;
      const href = a.getAttribute('href');
      if (!href || href === '#') return;
      e.preventDefault();
      if (pathname === '/') {
        if (window.__lenis) window.__lenis.scrollTo(href, { offset: -80 });
        else document.querySelector(href)?.scrollIntoView({ behavior: 'smooth' });
      } else {
        navigate('/' + href);
      }
    };
    document.addEventListener('click', onClick);
    return () => document.removeEventListener('click', onClick);
  }, [pathname, navigate]);
  return null;
};

const LenisRoot = () => {
  useEffect(() => {
    const lenis = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
    });
    window.__lenis = lenis;
    function raf(time) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }
    requestAnimationFrame(raf);
    return () => {
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);
  return null;
};

function App() {
  return (
    <div className="App bg-white">
      <BrowserRouter>
        <LenisRoot />
        <ScrollManager />
        <AnchorHandler />
        <CartProvider>
          <QuoteProvider>
            <LanguageProvider>
              <SiteDataProvider>
                <SeoManager />
                <Header />
                <main>
                  <Suspense fallback={<PageLoader />}>
                    <Routes>
                      <Route path="/" element={<Home />} />
                      <Route path="/products" element={<ProductsHub />} />
                      <Route path="/products/:slug" element={<ProductDetail />} />
                      <Route path="/categories" element={<Categories />} />
                      <Route path="/payment/success" element={<PaymentSuccess />} />
                      <Route path="/payment/cancel" element={<PaymentCancel />} />
                      <Route path="/blog" element={<BlogListing />} />
                      <Route path="/blog/:slug" element={<BlogDetail />} />
                      <Route path="/privacy-policy" element={<LegalPage />} />
                      <Route path="/terms-of-service" element={<LegalPage />} />
                      <Route path="/about-us" element={<AboutUs />} />
                      <Route path="/quality" element={<QualityManufacturing />} />
                      <Route path="/quality-and-manufacturing" element={<QualityManufacturing />} />
                      <Route path="/downloads" element={<Downloads />} />
                      <Route path="/faqs" element={<Faqs />} />
                      <Route path="/contact" element={<ContactUs />} />
                      <Route path="/request-quote" element={<RequestQuotePage />} />
                      <Route path="/category/:slug" element={<CategoryDetail />} />
                      <Route path="/specialty/:slug" element={<SpecialtyInstruments />} />
                      <Route path="*" element={<NotFound />} />
                    </Routes>
                  </Suspense>
                </main>
                <Footer />
                <WhatsAppWidget />
                <Toaster position="top-right" richColors />
              </SiteDataProvider>
            </LanguageProvider>
          </QuoteProvider>
        </CartProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
